'use client';

import clsx from 'clsx';
import type { CSSProperties, ForwardedRef } from 'react';
import { useId } from 'react-aria';
import {
  Provider as RACProvider,
  TextContext as RACTextContext,
  ProgressBar as RACProgressBar,
  type ProgressBarProps as RACProgressBarProps,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { withStyleProps } from '../../with-style-props.js';
import { FormFieldDescription, FormFieldLabel } from '../internal/form-field/form-field.js';
import type { CommonProps, LabellableProps } from '../types.js';
import { textStyle } from '../typography/utils.js';

export interface ProgressBarProps
  extends CommonProps, LabellableProps, Pick<RACProgressBarProps, 'value' | 'valueLabel' | 'maxValue'> {
  /**
   * The tone of the progress bar.
   * @default 'base'
   */
  tone?: 'base' | 'success' | 'warning' | 'critical';
  /**
   * Whether the value of the progress bar should be displayed above it.
   * @default true
   */
  showValue?: boolean;
  /** A description for the progress bar. */
  description?: string;
}

function ProgressBar(
  { label, description, tone = 'base', showValue = true, UNSAFE_className, UNSAFE_style, ...props }: ProgressBarProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const descriptionId = useId();

  return (
    <RACProvider values={[[RACTextContext, { slots: { description: { id: descriptionId } } }]]}>
      <RACProgressBar
        {...props}
        ref={ref}
        className={clsx('cim-progress-bar', UNSAFE_className)}
        style={UNSAFE_style}
        aria-describedby={[description && descriptionId, props['aria-describedby']].filter(Boolean).join(' ')}
      >
        {({ percentage, valueText }) => (
          <>
            <FormFieldLabel isRequired={false}>{label}</FormFieldLabel>
            {showValue && (
              <span className={clsx('cim-progress-bar-value', textStyle({ variant: 'medium', tone: 'base' }))}>
                {valueText}
              </span>
            )}
            <div className="cim-progress-bar-track">
              <div
                className="cim-progress-bar-fill"
                style={
                  {
                    '--cim-progress-bar-percentage': percentage + '%',
                    '--cim-progress-bar-fill-color': `var(--cim-bg-${tone === 'base' ? 'accent' : tone})`,
                  } as CSSProperties
                }
              />
            </div>
            <FormFieldDescription>{description}</FormFieldDescription>
          </>
        )}
      </RACProgressBar>
    </RACProvider>
  );
}

/**
 * Visualizes the progress of an operation.
 *
 * See [progress bar usage guidelines](https://ui.cimpress.io/components/progress-bar/).
 */
const _ProgressBar = withStyleProps(forwardRef(ProgressBar), 'ProgressBar');

export { _ProgressBar as ProgressBar };
